import { cloneDeep, isEqual } from "lodash-es";

export const chunkArray = (arr: any[], chunkSize: number) => {
  const cloneArr = cloneDeep(arr);
  const res = [];
  while (cloneArr.length > 0) {
    const chunk = cloneArr.splice(0, chunkSize);
    res.push(chunk);
  }

  return res;
};

export const mergeArray = (curr: any[]) => (newArr: any[]) => {
  newArr.forEach((b, i) => {
    curr[i] = { ...curr[i], ...b };
  });

  return [...curr];
};

export const paginate = <T = any>(
  array: T[] | undefined,
  pageSize: number,
  currentPage: number
) => {
  return array?.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  ) as T[];
};

export const sortArrayByField = <T>(
  arr: any[],
  field: T extends never ? string : keyof T,
  isAsc: boolean = true
) => {
  return arr?.slice()?.sort((a, b) => {
    const aField = a[field as any]! || "";
    const bField = b[field as any]! || "";

    if (aField > bField) {
      return isAsc ? 1 : -1;
    }
    if (aField < bField || !aField) {
      return isAsc ? -1 : 1;
    }

    return 0;
  });
};

export const isSubArray = (parent: any[], sub: any[]) => {
  for (let i = 0; i < sub.length; i++) {
    if (!parent.includes(sub[i])) {
      return false;
    }
  }

  return true;
};

export const sortDocumentTemplateTree = (arr: any[]) => {
  return arr?.slice()?.sort((a, b) => {
    const aTitle = a?.parentName?.trim() || "";
    const bTitle = b?.parentName?.trim() || "";
    const aUpdateDate = new Date(a?.updatedAt || "");
    const bUpdateDate = new Date(b?.updatedAt || "");
    const aCreateDate = new Date(a?.createdAt || "");
    const bCreateDate = new Date(b?.createdAt || "");

    if (aTitle !== bTitle) {
      return aTitle.localeCompare(bTitle);
    } else if (aUpdateDate.getTime() !== bUpdateDate.getTime()) {
      return aUpdateDate.getTime() - bUpdateDate.getTime();
    } else {
      return aCreateDate.getTime() - bCreateDate.getTime();
    }
  });
};

export const sortDocumentList = (arr: any[], isSortByLevel = false) => {
  return arr?.slice()?.sort((a, b) => {
    const aTitle = a?.title?.trim() || a?.templateName?.trim() || "";
    const bTitle = b?.title?.trim() || b?.templateName?.trim() || "";
    const aUpdateDate = new Date(a?.updatedAt || "");
    const bUpdateDate = new Date(b?.updatedAt || "");
    const aCreateDate = new Date(a?.createdAt || "");
    const bCreateDate = new Date(b?.createdAt || "");
    const aLevel = a?.level?.trim() || "";
    const bLevel = b?.level?.trim() || "";

    if (aTitle !== bTitle) {
      return aTitle.localeCompare(bTitle);
    } else if (aLevel !== bLevel && isSortByLevel) {
      return aLevel.localeCompare(bLevel);
    } else if (aUpdateDate.getTime() !== bUpdateDate.getTime()) {
      return aUpdateDate.getTime() - bUpdateDate.getTime();
    } else {
      return aCreateDate.getTime() - bCreateDate.getTime();
    }
  });
};

export const compareArray = (
  firstArray: Array<any>,
  secondArray: Array<any>
) => {
  if (!firstArray || !secondArray || firstArray.length !== secondArray.length) {
    return false;
  }

  return firstArray.every((itemInFirstArray) => {
    return secondArray.some((itemInSecondArray) =>
      isEqual(itemInSecondArray, itemInFirstArray)
    );
  });
};

/**
 * This function check if the first array contains the second array
 * @param firstArray - An array of any type.
 * @param secondArray - An array of any type.
 */
export const containsArray = (
  firstArray: Array<any>,
  secondArray: Array<any>
) => {
  if (!firstArray || !secondArray) {
    return false;
  }

  return secondArray.every((itemInSecondArray) => {
    return firstArray?.some((itemInFirstArray) =>
      isEqual(itemInSecondArray, itemInFirstArray)
    );
  });
};

/**
 * This function sorts an array of objects based on multiple sorting criteria and orders.
 * @param list - An array of elements to be sorted.
 * @param callbacks - An optional array of callback functions that define the sorting logic for each
 * column.
 */
export const multiSort = <T>(
  list: Array<T>,
  callbacks?: Array<(a: T, b: T) => number>
): T[] => {
  const sortedList = cloneDeep(list);

  if (!callbacks) {
    return sortedList;
  }

  callbacks?.forEach((callback) => {
    sortedList?.sort(callback);
  });

  return sortedList;
};

/**
 * This function takes an array as input and returns a new array with only unique
 * elements.
 * @param array - The `array` parameter is an array of any type `T`.
 */
export const uniqueArray = <T = any>(array: Array<T>) => {
  return array.reduce((acc: Array<T>, item: T) => {
    const isAccumulatorContainsItem = acc?.some((accItem) =>
      isEqual(accItem, item)
    );

    if (!isAccumulatorContainsItem) {
      acc?.push(item);
    }

    return acc;
  }, []);
};

export const ignoreArrayOrderCompare = (a: any[], b: any[]) => {
  if (a.length !== b.length) return false;
  const elements = new Set([...a, ...b]);
  for (const x of elements) {
    const count1 = a.filter((e) => e === x).length;
    const count2 = b.filter((e) => e === x).length;
    if (count1 !== count2) return false;
  }

  return true;
};

export const sortArrayByTitle = (arr: any[], title: string) => {
  if (!arr?.length) return [];

  const arrNoTitle = arr.filter((item) => !item?.[title]);
  const arrHasTitle = arr
    .filter((item) => item?.[title])
    .slice()
    .filter((item) => !!item)
    .sort((item1, item2) => {
      const title1 = String(item1?.[title]!)?.split("-");
      const title2 = String(item2?.[title]!)?.split("-");

      const length = Math.min(title1.length, title2.length);

      for (let i = 0; i < length; i++) {
        if (title1[i] === title2[i]) continue;
        if (!isNaN(Number(title1[i])) && !isNaN(Number(title2[i]))) {
          return Number(title1[i]) - Number(title2[i]);
        }

        return title1[i] < title2[i] ? -1 : 1;
      }

      return title1.length - title2.length;
    });

  return [...arrNoTitle, ...arrHasTitle];
};

export const updateElementInArray = <T>({
  array,
  element,
  keyIndex,
}: {
  array: T[];
  element: T | (() => T);
  keyIndex: keyof T | ((item: T, dataUpdate: T) => boolean);
}) => {
  const dataUpdate = element instanceof Function ? element() : element;
  if (!dataUpdate || !array?.length) {
    return array;
  }

  const index = array.findIndex((item) =>
    keyIndex instanceof Function
      ? keyIndex(item, dataUpdate)
      : item[keyIndex] === dataUpdate[keyIndex]
  );

  if (index === -1) {
    return array;
  }

  array[index] = { ...array[index], ...dataUpdate };

  return array;
};

export const toggleElementInArray = (
  array: string[] | number[],
  val: string | number,
  toggleVal: boolean
) => {
  const index = array.findIndex((id) => id === val);
  if (toggleVal && index < 0) {
    //@ts-ignore
    array.push(val);
  } else if (!toggleVal && index >= 0) {
    array.splice(index, 1);
  }
};

export const groupByKey = <T>(
  list: T[],
  key: keyof T,
  defaultGroup?: string
): { [key: string]: T[] } =>
  list.reduce((hash: { [key: string]: T[] }, obj) => {
    const k = obj[key] as any;

    return {
      ...hash,
      [k || defaultGroup]: (hash?.[k || defaultGroup] || []).concat(obj),
    };
  }, {});

enum SortPriority {
  number = "number",
  alphabet = "alphabet",
  hiragana = "hiragana",
  katakana = "katakana",
  kanji = "kanji",
  symbol = "symbol",
  empty = "empty",
}

const typeOrder = {
  [SortPriority.number]: 1,
  [SortPriority.alphabet]: 2,
  [SortPriority.hiragana]: 3,
  [SortPriority.katakana]: 4,
  [SortPriority.kanji]: 5,
  [SortPriority.symbol]: 6,
  [SortPriority.empty]: 7,
};

/*
 * カスタマイズソート関数。以下のロジックでソートする
 * 1. 数値（ゼロ埋めを考慮）
 * 例: 001_ABC, 010_ABC, 100_ABC ,101_ABC ,201_ABC
 * 数値部分が先頭にある場合、ゼロ埋め or 数値としてソート（→ "001" < "010" < "100"）
 * 2. 英字（アルファベット）
 * A〜Z の 昇順（大文字小文字を無視）
 * abc, ABC, Abc → 同じグループと見なす
 * 3. 日本語（ひらがな・カタカナ・漢字）
 * 五十音順（かな順）でソート
 * あ < い < う ... ん
 * カタカナはひらがなと同一視
 * 漢字は読みでソート（ふりがながない場合はUnicode順）
 * 4. 記号・その他
 * 原則として最後に配置（または独自ルールで別管理）
 */
export function customSortByColumn(arr: any[], columnName: string) {
  function getCharType(char: any) {
    if (char === undefined) {
      return SortPriority.empty;
    }
    const code = char.charCodeAt(0);

    if (code >= 0x30 && code <= 0x39) {
      return SortPriority.number;
    }
    if ((code >= 0x41 && code <= 0x5a) || (code >= 0x61 && code <= 0x7a)) {
      return SortPriority.alphabet;
    }
    if (code >= 0x3040 && code <= 0x309f) {
      return SortPriority.hiragana;
    }
    if (code >= 0x30a0 && code <= 0x30ff) {
      return SortPriority.katakana;
    }
    if (code >= 0x4e00 && code <= 0x9fff) {
      return SortPriority.kanji;
    }

    return SortPriority.symbol;
  }

  function extractPaddedNumber(str: any) {
    const s = String(str);
    const match = s.match(/^(\d+)/);
    if (match) {
      return match[1];
    }

    return null;
  }

  arr.sort((a, b) => {
    const valA = a[columnName];
    const valB = b[columnName];

    if (valA === undefined || valA === null) return 1;
    if (valB === undefined || valB === null) return -1;

    let i = 0;
    while (i < String(valA).length || i < String(valB).length) {
      const charA = String(valA)[i];
      const charB = String(valB)[i];

      const typeA = getCharType(charA);
      const typeB = getCharType(charB);

      if (typeA === SortPriority.empty && typeB !== SortPriority.empty) {
        return -1;
      }
      if (typeA !== SortPriority.empty && typeB === SortPriority.empty) {
        return 1;
      }
      if (typeA === SortPriority.empty && typeB === SortPriority.empty) {
        return 0;
      }

      if (typeOrder[typeA] !== typeOrder[typeB]) {
        return typeOrder[typeA] - typeOrder[typeB];
      }

      switch (typeA) {
        case SortPriority.number:
          const numAStr = extractPaddedNumber(String(valA).substring(i));
          const numBStr = extractPaddedNumber(String(valB).substring(i));

          if (numAStr !== null && numBStr !== null) {
            const numA = parseInt(numAStr, 10);
            const numB = parseInt(numBStr, 10);

            if (numA !== numB) {
              return numA - numB;
            } else {
              if (numAStr.length !== numBStr.length) {
                return numAStr.length - numBStr.length;
              }
              i += numAStr.length;
              continue;
            }
          }
          break;
        case SortPriority.alphabet:
          const lowerA = charA.toLowerCase();
          const lowerB = charB.toLowerCase();
          if (lowerA !== lowerB) {
            return lowerA.localeCompare(lowerB);
          }
          break;
        case SortPriority.hiragana:
        case SortPriority.katakana:
          if (charA !== charB) {
            return charA.localeCompare(charB, "ja", {
              sensitivity: "base",
            });
          }
          break;
        case SortPriority.kanji:
          if (charA !== charB) {
            return charA.localeCompare(charB, "ja");
          }
          break;
        case SortPriority.symbol:
          if (charA !== charB) {
            return charA.localeCompare(charB);
          }
          break;
      }
      i++;
    }

    return 0;
  });

  return arr;
}

export function sortByColumn(newRecords: any[], col: string, isAsc: boolean) {
  const collator = new Intl.Collator("ja", { sensitivity: "base" });
  const sorted = newRecords.sort((a, b) => {
    const valueA = a[col] || "";
    const valueB = b[col] || "";

    return collator.compare(valueA, valueB);
  });

  return isAsc ? sorted : sorted.reverse();
}
